import type Geometry from 'ol/geom/Geometry';
import GeometryCollection from 'ol/geom/GeometryCollection';
import LineString from 'ol/geom/LineString';
import MultiLineString from 'ol/geom/MultiLineString';
import MultiPolygon from 'ol/geom/MultiPolygon';
import Polygon from 'ol/geom/Polygon';
import { toLonLat } from 'ol/proj';
import { getArea, getDistance, getLength } from 'ol/sphere';

export const NICARAGUAN_MANZANA_M2 = 7_042.25;
const MAP_PROJECTION = 'EPSG:3857';

export interface AreaMeasurement {
  readonly areaM2: number;
  readonly hectares: number;
  readonly manzanas: number;
  readonly perimeterM: number;
}

export function geodesicLength(geometry: Geometry): number {
  if (geometry instanceof LineString || geometry instanceof MultiLineString) return getLength(geometry, { projection: MAP_PROJECTION });
  if (geometry instanceof GeometryCollection) return geometry.getGeometries().reduce((sum, item) => sum + geodesicLength(item), 0);
  return 0;
}

/** Geodesic distance (m) between two map (EPSG:3857) coordinates: the same great-circle measure `geodesicLength` sums. */
export const geodesicDistance = (a: readonly number[], b: readonly number[]): number => getDistance(toLonLat([...a]), toLonLat([...b]));

const polygonPerimeter = (polygon: Polygon): number => polygon.getLinearRings().reduce((sum, ring) => sum + getLength(ring, { projection: MAP_PROJECTION }), 0);

/**
 * Geodesic results are memoised per geometry instance and revision: the same fragment is measured once
 * per edit instead of on every table, legend or classification refresh. Any coordinate change bumps the
 * OpenLayers revision and invalidates the entry.
 */
const memo = <T>(cache: WeakMap<Geometry, { readonly revision: number; readonly value: T }>, geometry: Geometry, compute: () => T): T => {
  const cached = cache.get(geometry); const revision = geometry.getRevision();
  if (cached && cached.revision === revision) return cached.value;
  const value = compute(); cache.set(geometry, { revision, value }); return value;
};
const areaCache = new WeakMap<Geometry, { readonly revision: number; readonly value: number }>();
const perimeterCache = new WeakMap<Geometry, { readonly revision: number; readonly value: number }>();

export function geodesicArea(geometry: Geometry): number {
  return memo(areaCache, geometry, () => {
    if (geometry instanceof Polygon || geometry instanceof MultiPolygon) return Math.abs(getArea(geometry, { projection: MAP_PROJECTION }));
    if (geometry instanceof GeometryCollection) return geometry.getGeometries().reduce((sum, item) => sum + geodesicArea(item), 0);
    return 0;
  });
}

export function geodesicPerimeter(geometry: Geometry): number {
  return memo(perimeterCache, geometry, () => {
    if (geometry instanceof Polygon) return polygonPerimeter(geometry);
    if (geometry instanceof MultiPolygon) return geometry.getPolygons().reduce((sum, polygon) => sum + polygonPerimeter(polygon), 0);
    if (geometry instanceof GeometryCollection) return geometry.getGeometries().reduce((sum, item) => sum + geodesicPerimeter(item), 0);
    return 0;
  });
}

export function measureArea(geometry: Geometry): AreaMeasurement {
  const areaM2 = geodesicArea(geometry);
  return { areaM2, hectares: areaM2 / 10_000, manzanas: areaM2 / NICARAGUAN_MANZANA_M2, perimeterM: geodesicPerimeter(geometry) };
}

const localeNumber = (value: number, maximumFractionDigits: number): string => value.toLocaleString('es-NI', { maximumFractionDigits, minimumFractionDigits: maximumFractionDigits });

export const formatDistance = (metres: number): string => metres >= 1_000
  ? `${localeNumber(metres / 1_000, 2)} km`
  : `${localeNumber(metres, 1)} m`;

export const formatAreaMeasurement = (measurement: AreaMeasurement): string => {
  const primary = measurement.areaM2 >= 10_000 ? `${localeNumber(measurement.hectares, 2)} ha` : `${localeNumber(measurement.areaM2, 1)} m²`;
  return `${primary} · ${localeNumber(measurement.manzanas, 2)} mz · perímetro ${formatDistance(measurement.perimeterM)}`;
};

export function totalPolygonArea(geometries: readonly Geometry[]): number {
  return geometries.reduce((sum, geometry) => sum + geodesicArea(geometry), 0);
}
